import React, { useState } from 'react';
import Draggable from 'react-draggable';
import CheckboxList from './NodesCheckList';
import ChartCrosshair from './chartjs-plugin-crosshair';
import { useEffect, useRef } from 'react';
import { Grid, Icon, Button } from 'semantic-ui-react';
import AnalysisPicker from './AnalysisPicker';
import {
    Chart as ChartJS,
    CategoryScale,    
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors // Add Colors plugin
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    ChartCrosshair,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors // Register Colors plugin
);

// Add default colors configuration
ChartJS.defaults.plugins.colors = {
    enabled: true,
    forceOverride: true
};

// Add fallback colors
const defaultColors = [
    '#4dc9f6',
    '#f67019',
    '#f53794',
    '#537bc4',
    '#acc236',
    '#166a8f',
    '#00a950',
    '#58595b',
    '#8549ba'
];

// Add error handling for datasets
const safeDatasets = (datasets) => {
    if (!datasets || !Array.isArray(datasets)) return [];
    
    return datasets.map((dataset, index) => {
        return {
            ...dataset,
            backgroundColor: dataset.backgroundColor || defaultColors[index % defaultColors.length],
            borderColor: dataset.borderColor || defaultColors[index % defaultColors.length],
            pointBackgroundColor: dataset.pointBackgroundColor || defaultColors[index % defaultColors.length]
        };
    });
};


  const annotation = {
    annotations: [{
        type: 'line',
        mode: 'vertical',
        scaleID: 'x-axis',
        borderColor: '#b6fcd5',
        borderWidth: 2
    }]
};

  
const Chart = (props) => {
    // State to track the position of the draggable element
    
    const chartAmpRef = useRef(null);
    const chartVoltRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [isChartLoading, setChartLoading] = useState(false);        
    const [dcSweepValue, setDcSweepValue] = useState(null);
    
    // Persistent state keys
    const DISPLAY_MODE_KEY = 'chart_displayMode';
    const PREVIOUS_MODE_KEY = 'chart_previousMode';
    const ORIGINAL_DIMENSIONS_KEY = 'chart_originalDimensions';

    // State for chart display mode: 'normal', 'small', 'minimized'
    const [displayMode, setDisplayMode] = useState(() => {
        return localStorage.getItem(DISPLAY_MODE_KEY) || 'normal';
    });
    // Store previous mode when minimizing
    const [previousMode, setPreviousMode] = useState(() => {
        return localStorage.getItem(PREVIOUS_MODE_KEY) || 'normal';
    });
    // Store original dimensions
    const [originalDimensions, setOriginalDimensions] = useState(() => {
        const stored = localStorage.getItem(ORIGINAL_DIMENSIONS_KEY);
        return stored ? JSON.parse(stored) : { width: '50%', left: '25%' };
    });

    // Persist state changes to localStorage
    useEffect(() => {
        localStorage.setItem(DISPLAY_MODE_KEY, displayMode);
    }, [displayMode]);

    useEffect(() => {
        localStorage.setItem(PREVIOUS_MODE_KEY, previousMode);
    }, [previousMode]);

    useEffect(() => {
        localStorage.setItem(ORIGINAL_DIMENSIONS_KEY, JSON.stringify(originalDimensions));
    }, [originalDimensions]);

    // Update the position state on drag stop
    const handleDragStop = (e, data) => {    
        window.thumbs.lab("setSelectionOff");        
        window.thumbs.lab("setPlotLocation", { x: data.x, y: data.y });
        window.thumbs.lab("setCircuitOnOff", "on");        
    };

    const handleDragStart = (e, data) => {    
      window.thumbs.lab("setCircuitOnOff", "off");      
    };
    

    
    const checkLabels = (data) => {
      // Check if the datasets key exists and is an array
      if (!data.datasets || !Array.isArray(data.datasets)) {
          return "";
      }
  
      // Check if all labels start with "I("
      const allStartWithI = data.datasets.every(dataset => 
          typeof dataset.label === 'string' && dataset.label.startsWith("I(")
      );
  
      if (allStartWithI) return "A";
  
      // Check if all labels start with "V"
      const allStartWithV = data.datasets.every(dataset => 
          typeof dataset.label === 'string' && dataset.label.startsWith("V")
      );
  
      if (allStartWithV) return "V";
  
      // If neither condition is met, return an empty string
      return "";
    }

    let sweep_options={
      plugins: {
        crosshair: {
            line: {
              color: '#F66', // Crosshair line color
              width: 1 // Crosshair line width
            },
            sync: {
              enabled: true, // Enable trace line syncing across multiple charts
              group: 3, // Sync group
              suppressTooltips: false // Do not suppress tooltips
            }
          },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: function(tooltipItem, data) {            
            tooltipItem.label = tooltipItem.label;                
            tooltipItem.formattedValue = tooltipItem.formattedValue;                
            return data;
          }
        }
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,            
        text: '',
      },
    },
      scales: {
        x: {
          _ticks: {
            // Include a dollar sign in the ticks
            callback: function(value, index, ticks) {
                  return window.thumbs.correctAndRoundNumber(value) +"V";
              }
          },
          grid: {
            drawOnChartArea: true, // Remove grid lines from chart area
          }
        },
        y: {
          ticks: {
            // Include a dollar sign in the ticks
            callback: function(value, index, ticks) {
                  return window.thumbs.correctAndRoundNumber(value) + checkLabels(props.chart_VSweep_values);//VERTICAL AXIS LABELS
              }
          },
          grid: {
            drawBorder: true, // Optionally, remove border around Y axis
          }
        }
      }
    }


    let volts_options={
      plugins: {
        crosshair: {
            line: {
              color: '#F66', // Crosshair line color
              width: 1 // Crosshair line width
            },
            sync: {

              enabled: true, // Enable trace line syncing across multiple charts
              group: 1, // Sync group
              suppressTooltips: false // Do not suppress tooltips
            }
          },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        position: 'nearest',
        callbacks: {
          label: function(tooltipItem, data) {            
            tooltipItem.label = tooltipItem.label + "S";                
            tooltipItem.formattedValue = tooltipItem.formattedValue + "V";                
            return data;
          }
        }
      },
      legend: {
        position: 'top',
      },
      title: {
        display: false,            
        text: '',
      },
    },
      scales: {
        x: {
          grid: {
            drawOnChartArea: true, // Remove grid lines from chart area
          }
        },
        y: {
          ticks: {
            // Include a dollar sign in the ticks
            callback: function(value, index, ticks) {
                  return window.thumbs.correctAndRoundNumber(value) + "V";
              }
          },
          grid: {
            drawBorder: true, // Optionally, remove border around Y axis
          }
        }
      }
    }

    


    let ampere_options={
      plugins: {
            crosshair: {
                line: {
                  color: '#F66', // Crosshair line color
                  width: 1 // Crosshair line width
                },
                sync: {

                  enabled: true, // Enable trace line syncing across multiple charts
                  group: 1, // Sync group
                  suppressTooltips: false // Do not suppress tooltips
                }
              },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            position: 'nearest',
            callbacks: {
              label: function(tooltipItem, data) {                
                tooltipItem.label = tooltipItem.label + "S";                
                tooltipItem.formattedValue = tooltipItem.formattedValue + "A";                
                return data;
              }
            }
          },
          legend: {
            position: 'top',
          },
          title: {
            display: false,            
            text: '',
          },
        },
      scales: {
        x: {
          grid: {
            drawOnChartArea: true, // Remove grid lines from chart area
          }
        },
        y: {
          ticks: {
            // Include a dollar sign in the ticks
            callback: function(value, index, ticks) {
                  return window.thumbs.correctAndRoundNumber(value) + "A";
              }
          },
          grid: {
            drawBorder: true, // Optionally, remove border around Y axis
          }
        }
      }
    }

    const options = {    
          
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },                      
        elements: {
          point: {
            radius: 0 // Hide the points by setting radius to 0
          },
          line: {
            tension: 0.4 // Adjust the tension for smoothness
          }
        },
      };

      volts_options={...volts_options, ...options}
      ampere_options={...ampere_options, ...options}
      
      
      const moveCrosshairToIndex = (index) => {
        
        if (props.chart_amperes_data.datasets.length === 0) return;
        if (!chartAmpRef.current) return;        
        
        const chart = chartAmpRef.current;
        const xScale = chart.scales['x'];
        const yScale = chart.scales['y'];
        if (xScale == null || yScale == null) return;
        const xValue = xScale.getPixelForValue(index);
        const yValue = yScale.getPixelForValue(props.chart_amperes_data.datasets[0].data[index]);
        try {
          const event = new MouseEvent('mousemove', {
            clientX: xValue + chart.canvas.getBoundingClientRect().left,
            clientY: yValue + chart.canvas.getBoundingClientRect().top,
          });
    
          chart.canvas.dispatchEvent(event);
        } catch (exceptionVar) {
          
        }
        
      };

      window.moveCrosshairToIndex = moveCrosshairToIndex;      
















      useEffect(() => {        
        setChartLoading(false);        
    }, [props.selectedNodesValues]); // Dependency array with 'props.selectedNodesValues'


    return (
      <Draggable
          onStop={handleDragStop}
          onStart={handleDragStart}            
          defaultPosition={props.initialLocation}
          bounds='parent'
      >
          <div               
              className="draggable-container"
              style={{ 
                  pointerEvents: props.currentMouseEvent === 'up' ? 'auto' : 'none',
                  position: 'absolute', 
                  zIndex: 100,                  
                  width: displayMode === 'small' ? '30%' : displayMode === 'minimized' ? '200px' : '50%', // Width based on display mode
                  left: displayMode === 'small' ? '35%' : displayMode === 'minimized' ? '40%' : '25%', // Adjust position based on display mode
                  padding: 0, // Make sure there's no padding
                  margin: 0, // Make sure there's no margin
                  marginTop: '0px', // Removed the top margin to fix spacing issue
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  transition: 'width 0.3s ease, left 0.3s ease', // Smooth transition for size changes
              }}
          >
            <Grid style={{margin: 0}}>
              <Grid.Column width={16} style={{backgroundColor: '#eeeeee', padding: 0, margin: 0 }}>                
              <div style={{cursor: 'move', backgroundColor: 'burlywood', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', height: '42px', borderBottom: '1px solid #ccc'}}> 
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  {displayMode !== 'minimized' && (
                    <AnalysisPicker                                                         
                      analysisMode={props.analysisMode} 
                      setAnalysisMode={props.setAnalysisMode} 
                      dcSweepValue={props.pickedDCSweepSource} 
                      setDcSweepValue={props.setPickedDcSweepSource} 
                      dcSweepOptions={props.DCSweepSources}
                      setNodes={props.setNodes}
                    />
                  )}
                  {displayMode === 'minimized' && (
                    <div style={{ fontWeight: 'bold' }}>Chart (Minimized)</div>
                  )}
                </div>
                <div style={{ display: 'flex', padding: '0 0 0 12px' }}>
                  {/* Buttons in order: resize, minimize, close */}
                  {displayMode !== 'minimized' && (
                    <Button 
                      icon 
                      size='mini' 
                      style={{ marginRight: '8px', padding: '0', height: '28px', width: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                      onClick={() => {
                        if (displayMode === 'normal') {
                          setDisplayMode('small');
                        } else {
                          setDisplayMode('normal');
                        }
                      }}
                    >
                      <Icon name={displayMode === 'small' ? 'expand' : 'compress'} style={{ margin: '0' }} />
                    </Button>
                  )}
                  
                  {/* Minimize/Maximize button */}
                  <Button 
                    icon 
                    size='mini' 
                    style={{ marginRight: '8px', padding: '0', height: '28px', width: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={() => {
                      if (displayMode === 'minimized') {
                        setDisplayMode(previousMode);
                      } else {
                        setPreviousMode(displayMode);
                        setDisplayMode('minimized');
                      }
                    }}
                  >
                    <Icon name={displayMode === 'minimized' ? 'window maximize' : 'window minimize'} style={{ margin: '0' }} />
                  </Button>
                  
                  {/* Close button - keeping only this one */}
                  <Button 
                    icon 
                    size='mini' 
                    style={{ padding: '0', height: '28px', width: '28px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={() => {
                      // Close the chart properly
                      props.setNodes([]);
                      com.clientside.main.Html5Bootstrap.jsCall("setNGSpiceOff");
                    }}
                  >
                    <Icon name='close' style={{ margin: '0' }} />
                  </Button>
                </div>
              </div>

              </Grid.Column>
              </Grid>  

              {displayMode !== 'minimized' && (
                <Grid style={{marginTop: 0}}>
                  {/* Left Column with Title */}
                  
                  <Grid.Column width={2} style={{backgroundColor: '#eeeeee', padding: '10px 10px 10px 15px', margin: 0 }}>                  
                    {props.analysisMode === 'time_domain' &&    
                      <CheckboxList   
                      top={5}
                      label='Amps'    
                      role={'component'}                                            
                      initialSelectedLabels={props.initialSelectedLabels}
                      setChartLoading={props.setChartLoading}
                      isChartLoading={props.isChartLoading}
                      selectedNodesValues={props.selectedNodesValues}                      
                      setSelectedNodes={props.setSelectedNodes}
                      setSelectedNodesValues={props.setSelectedNodesValues}
                      nodes={props.currentable_names}
                      />
                    }


                  </Grid.Column>
  
                  {/* Center Column */}
                  <Grid.Column width={14} style={{ padding: '0 0 0 0', margin: 0 }}>
                      <div 
                          className="chart-container"
                          style={{                               
                              backgroundColor: props.currentMouseEvent === 'up' ? '#FFF9C4' : '#FFF9C4',
                              padding: '0',
                              borderRadius: '4px'
                          }}
                      >                          
                          {props.analysisMode === 'time_domain' &&
                          <Line 
                              ref={chartAmpRef} 
                              key="chart_1" 
                              style={{maxHeight: 200, height: 200}} 
                              options={ampere_options} 
                              data={{
                                  ...props.chart_amperes_data,
                                  datasets: safeDatasets(props.chart_amperes_data?.datasets)
                              }} 
                          />
                          }                          
                      </div>
                  </Grid.Column>

              </Grid>
              )}
              {props.analysisMode === 'time_domain' && displayMode !== 'minimized' && (
                <Grid>
                  {/* Left Column with Title */}
                  <Grid.Column width={2} style={{backgroundColor: '#eeeeee', padding: '10px 10px 10px 15px', margin: 0 }}>
                      
                    <CheckboxList         
                    top={0}
                    style={{position:'relative'}}
                    role={'volt'}                                    
                    label='Volts'                    
                    initialSelectedLabels={props.initialSelectedLabels}
                    setChartLoading={props.setChartLoading}
                    isChartLoading={props.isChartLoading}
                    selectedNodesValues={props.selectedNodesValues}                  
                    setSelectedNodes={props.setSelectedNodes}
                    setSelectedNodesValues={props.setSelectedNodesValues}
                    nodes={props.nodes}
                    />
                    </Grid.Column>
  
                  {/* Center Column */}
                  <Grid.Column width={14} style={{ padding: '0px 0px 0px 0', margin: 0 }}>
                      <div 
                          className="chart-container"
                          style={{                               
                              backgroundColor: props.currentMouseEvent === 'up' ? '#D7EAF9' : '#D7EAF9',
                              padding: '0',
                              borderRadius: '4px'
                          }}
                      >                          
                      <Line 
                          ref={chartVoltRef} 
                          key="chart_2" 
                          style={{maxHeight: 200, height: 200}} 
                          options={volts_options} 
                          data={{
                              ...props.chart_volts_data,
                              datasets: safeDatasets(props.chart_volts_data?.datasets)
                          }} 
                      />
                          
                      </div>
                  </Grid.Column>

  
                  
              </Grid>
              )}

              {props.analysisMode === 'dc_sweep' && displayMode !== 'minimized' && (
              <div>              
              <Grid>
                  {/* Left Column with Title */}
                  <Grid.Column width={2} style={{marginTop: 0, backgroundColor: '#eeeeee', padding: '10px 10px 10px 15px' }}>
                      
                    <CheckboxList         
                    top={5}
                    style={{position:'relative'}}
                  role={'metric'}                  
                  label='Metric'    
                  initialSelectedLabels={props.initialSelectedLabels}
                  setChartLoading={props.setChartLoading}
                  isChartLoading={props.isChartLoading}
                  selectedNodesValues={props.selectedNodesValues}                  
                  setSelectedNodes={props.setSelectedNodes}
                  setSelectedNodesValues={props.setSelectedNodesValues}
                  nodes={props.metricNodes}
                  />
                  </Grid.Column>
  
                  {/* Center Column */}                  
                  <Grid.Column width={14} style={{ padding: '0px 0px 0px 0', margin: 0 }}>
                      <div 
                          className="chart-container"
                          style={{                               
                              marginTop: 0, backgroundColor: props.currentMouseEvent === 'up' ? '#D7EAF9' : '#D7EAF9',
                              padding: '0',
                              borderRadius: '4px'
                          }}
                      >                        
                      <Line 
                          key="chart_3" 
                          style={{maxHeight: 400, height: 400}} 
                          options={sweep_options} 
                          data={{
                              ...props.chart_VSweep_values,
                              datasets: safeDatasets(props.chart_VSweep_values?.datasets)
                          }} 
                      />
                          
                      </div>
                  </Grid.Column>

  
                  
              </Grid>
              </div>
              )}
              
              {/* Show a message when minimized */}
              {displayMode === 'minimized' && (
                <div style={{ 
                  padding: '10px', 
                  textAlign: 'center', 
                  backgroundColor: '#f8f8f8',
                  borderTop: '1px solid #ddd',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Click the maximize button to restore
                </div>
              )}

          </div>
      </Draggable>
  );
};

export default Chart;




